import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  CallPool,
  CallPoolWithClinics,
  CallPoolPage,
  CallPoolSearchRequest,
  CreateCallPoolRequest,
  UpdateCallPoolRequest,
} from '../../models/Settings/CallPool';
import { Clinic } from '../../models/MedVox/Clinic';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';

export interface CallPoolState {
  callPools: CallPool[];
  status: Status;
  callPoolItems: CallPoolWithClinics[];
  totalItemsCount: number;
  saving: boolean;
  poolClinicsMap: Record<string, Clinic[]>;
  poolClinicsLoading: Record<string, boolean>;
}

const initialState: CallPoolState = {
  callPools: [],
  status: {
    responseLoading: false,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
  callPoolItems: [],
  totalItemsCount: 0,
  saving: false,
  poolClinicsMap: {},
  poolClinicsLoading: {},
};

export const getCallPools = createAsyncThunk(
  'callPool/getCallPools',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<CallPool[]>('callpool');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Не удалось загрузить пулы обзвона!');
    }
  },
);

export const searchCallPools = createAsyncThunk(
  'callPool/searchCallPools',
  async (request: CallPoolSearchRequest, { rejectWithValue }) => {
    try {
      const params: Record<string, unknown> = {
        PageIndex: request.PageIndex,
        PageSize: request.PageSize,
      };
      if (request.TextSearch) {
        params['Filter.TextSearch'] = request.TextSearch;
      }
      const response = await apiClientWithApi.get<CallPoolPage>(
        'callpool/search',
        { params },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось загрузить пулы обзвона!');
    }
  },
);

export const createCallPool = createAsyncThunk(
  'callPool/createCallPool',
  async (data: CreateCallPoolRequest, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.post<string>('callpool', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось создать пул обзвона!');
    }
  },
);

export const updateCallPool = createAsyncThunk(
  'callPool/updateCallPool',
  async (
    { callPoolId, data }: { callPoolId: string; data: UpdateCallPoolRequest },
    { rejectWithValue },
  ) => {
    try {
      await apiClientWithApi.patch(`callpool/${callPoolId}`, data);
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось обновить пул обзвона!');
    }
  },
);

export const getCallPoolClinics = createAsyncThunk(
  'callPool/getCallPoolClinics',
  async (callPoolId: string, { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.get<Clinic[]>(
        `callpool/${callPoolId}/clinic`,
      );
      return { callPoolId, clinics: response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось загрузить клиники пула!');
    }
  },
);

export const callPoolSlice = createSlice({
  name: 'callPool',
  initialState,
  reducers: {
    clearCallPoolStatus(state) {
      state.status.requestSuccess = null;
      state.status.requestError = null;
    },
    updatePoolClinicsAfterSave(
      state,
      action: PayloadAction<{ poolId: string; clinics: Clinic[] }>,
    ) {
      const { poolId, clinics } = action.payload;
      const reassignedClinicIds = new Set(clinics.map((c) => c.id));

      // Set clinics for the saved pool
      state.poolClinicsMap[poolId] = clinics;

      // Remove reassigned clinics from all other pools
      for (const otherPoolId of Object.keys(state.poolClinicsMap)) {
        if (otherPoolId === poolId) continue;
        state.poolClinicsMap[otherPoolId] = state.poolClinicsMap[
          otherPoolId
        ].filter((c) => !reassignedClinicIds.has(c.id));
      }

      // Keep callPoolItems sorted by title
      state.callPoolItems.sort((a, b) => a.title.localeCompare(b.title));
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getCallPools.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(getCallPools.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.callPools = action.payload;
      })
      .addCase(getCallPools.rejected, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = action.payload as string;
      })
      .addCase(searchCallPools.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(searchCallPools.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.callPoolItems = [...action.payload.items].sort((a, b) =>
          a.title.localeCompare(b.title),
        );
        state.totalItemsCount = action.payload.totalItemsCount;
      })
      .addCase(searchCallPools.rejected, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = action.payload as string;
      })
      .addCase(createCallPool.pending, (state) => {
        state.saving = true;
        state.status.requestError = null;
      })
      .addCase(createCallPool.fulfilled, (state) => {
        state.saving = false;
        state.status.requestSuccess = 'Пул обзвона успешно создан';
      })
      .addCase(createCallPool.rejected, (state, action) => {
        state.saving = false;
        state.status.requestError = action.payload as string;
      })
      .addCase(updateCallPool.pending, (state) => {
        state.saving = true;
        state.status.requestError = null;
      })
      .addCase(updateCallPool.fulfilled, (state) => {
        state.saving = false;
        state.status.requestSuccess = 'Пул обзвона успешно обновлён';
      })
      .addCase(updateCallPool.rejected, (state, action) => {
        state.saving = false;
        state.status.requestError = action.payload as string;
      })
      .addCase(getCallPoolClinics.pending, (state, action) => {
        state.poolClinicsLoading[action.meta.arg] = true;
      })
      .addCase(getCallPoolClinics.fulfilled, (state, action) => {
        const { callPoolId, clinics } = action.payload;
        state.poolClinicsMap[callPoolId] = clinics;
        state.poolClinicsLoading[callPoolId] = false;
      })
      .addCase(getCallPoolClinics.rejected, (state, action) => {
        state.poolClinicsLoading[action.meta.arg] = false;
      });
  },
});

export const { clearCallPoolStatus, updatePoolClinicsAfterSave } =
  callPoolSlice.actions;

export default callPoolSlice.reducer;

export const selectCallPools = (state: RootState) => state.callPools.callPools;
export const callPoolsStatus = (state: RootState) => state.callPools.status;
export const selectCallPoolItems = (state: RootState) =>
  state.callPools.callPoolItems;
export const selectCallPoolTotalCount = (state: RootState) =>
  state.callPools.totalItemsCount;
export const selectCallPoolSaving = (state: RootState) =>
  state.callPools.saving;
export const selectPoolClinicsMap = (state: RootState) =>
  state.callPools.poolClinicsMap;
export const selectPoolClinicsLoading = (state: RootState) =>
  state.callPools.poolClinicsLoading;
